<template>
  <b-modal
    v-model="visible"
    :busy="busy"
    show
    size="xl"
    @ok="onOk"
    @hide="onHide"
  >
    <template #modal-title>
      <b-card-title>Opdracht</b-card-title>
      <b-card-sub-title>{{ opdrachtPath }}</b-card-sub-title>
    </template>
    <b-overlay 
      :show="busy" 
      variant="dark"
    >
      <b-row>
        <b-col 
          cols="4"
        >
          <b-form-row>
            <b-form-group label="Relatie">
              <b-input 
                v-if="opdracht.relatie !== null"
                v-model="opdracht.relatie.naam" 
                disabled
              />
            </b-form-group>
            <b-col>
              <b-form-group label="Accountmanager">
                <inspecteur-select
                  :value="opdracht.toegewezen_aan"
                  :allow-beheerders="true"
                  :eigen-groep-alleen="true"
                  @input="opdracht.toegewezen_aan = $event ? { Id: $event.id, Naam: $event.naam } : null"
                />
              </b-form-group>
            </b-col>
          </b-form-row>
          <b-form-row>
            <b-col>
              <b-form-group label="Offerte nummer">
                <b-input 
                  :value="opdracht.nummer" 
                  readonly
                />
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group label="Naam">
                <b-input 
                  :value="opdracht.naam" 
                  readonly
                />
              </b-form-group>
            </b-col>
          </b-form-row>
          <b-form-row>
            <b-col>
              <b-form-group label="Datum">
                <b-input 
                  :value="moment(opdracht.datum).format('L')" 
                  readonly
                />
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group label="Bedrag">
                <b-input 
                  :value="opdracht.bedrag" 
                  readonly 
                  type="number"
                />
              </b-form-group>
            </b-col>
          </b-form-row>

          <b-form-group label="Adres">
            <b-textarea 
              :value="opdracht.adres" 
              readonly
            />
          </b-form-group>

          <b-form-group label="Status">
            <b-radio-group
              v-model="opdracht.status"
              :options="['OPENSTAAND', 'OPDRACHT', 'GEREED', 'GEFACTUREERD']"
              buttons
              class="w-100"
            />
          </b-form-group>

          <b-form-group
            label="Activiteiten"
            description="De activiteiten zoals omschreven in de offerte"
          >
            <b-input
              :value="opdracht.activiteiten.join(', ')"
              readonly
              stacked
            />
          </b-form-group>

          <b-form-group label="Opdrachtnummer">
            <b-input
              v-model="opdracht.opdrachtnummer"
              placeholder="Het opdrachtnummer van de klant"
            />
          </b-form-group>

          <b-form-group 
            v-if="opdracht.bestand" 
            label="Offerte bestand"
          >
            <b-link 
              :href="opdracht.bestand.secure_url" 
              target="_blank"
            >
              <font-awesome-icon icon="download" /> Download bestand
            </b-link>
          </b-form-group>

          <b-form-group label="Opdracht bestand">
            <secure-upload
              v-if="opdracht.opdrachtbestand == null"
              :endpoint="authDocPath"
              subfolder="opdrachten"
              @file-uploaded="opdracht.opdrachtbestand = $event.document"
            />

            <b-button-toolbar v-if="opdracht.opdrachtbestand != null">
              <b-link
                :href="opdracht.opdrachtbestand.secure_url"
                target="_blank"
              >
                <font-awesome-icon icon="download" /> Download bestand
              </b-link>
            </b-button-toolbar>
          </b-form-group>
        </b-col>

        <b-col>
          <b-tabs v-model="selectedTab">
            <b-tab 
              title="Planning" 
              title-item-class="h5"
            >
              <b-table
                :items="opdracht.events"
                :fields="['status', 'notitie', {
                  key: 'data.unit.unitkenmerk',
                  label: 'Unit',
                  sortable: true,
                },'aan', 'deadline', 'actie']"
                primary-key="data.id"
                small
                @row-clicked="(item) => showDialogEdit(item)"
              >
                <template #cell(status)="row">
                  <font-awesome-icon 
                    v-if="row.item.data.status" 
                    icon="check"
                  />
                </template>
                <template #cell(#)="row">
                  {{ row.index + 1 }}
                </template>
                <template #cell(notitie)="row">
                  {{ row.item.data.type }} {{ row.item.data.moment }}<br>
                  <small>{{ row.item.data.omschrijving }}</small>
                </template>
                <template #cell(aan)="row">
                  {{
                    row.item.data.toegewezen_aan != null
                      ? row.item.data.toegewezen_aan.Naam
                      : ""
                  }}
                  <font-awesome-icon
                    v-if="row.item.data.gecontroleerd_door"
                    icon="user-check"
                  />
                </template>
                <template #cell(deadline)="row">
                  {{ moment(row.item.data.deadline).format("L") }}
                </template>
                <template #cell(actie)="row">
                  <b-button
                    v-if="row.item.data.type == 'Facturatie'"
                    :disabled="opdracht.status == 'GEFACTUREERD'"
                    @click="() => createInvoice({ 
                      amount: row.item.data.bedrag
                    })"
                  >
                    <i class="fa fa-external-link fa-lg mt-4" /> Genereer conceptfactuur 
                    {{ row.item.data.bedrag | formatNumber("currency") }} <br>en open in nieuwe tab
                  </b-button>
                </template>
              </b-table>

              <traject-menu 
                :disabled="opdracht.status == 'GEFACTUREERD'" 
                @action="insertTaak($event)"
              />
            </b-tab>

            <b-tab 
              title="Facturen" 
              title-item-class="h5"
            >
              [facturen]
            </b-tab>

            <b-tab
              v-if="opdracht.relatie !== null"
              title="Wizard uitvoering"
              title-item-class="h5"
            >
              <unit-select 
                v-model="selectedUnit"
                :relatie-id="opdracht.relatie.id"
                @unit-selected="(item) => selectedUnit = item"
              />

              <uitvoering-wizard 
                v-if="selectedUnit !== null"
                :traject-id="id"
                :unit="selectedUnit"
                @uitvoering-completed="onUitvoeringCompleted"
              />
            </b-tab>
          </b-tabs>
        </b-col>
      </b-row>
    </b-overlay>

    <b-modal
      v-if="inputStapObject != null"
      v-model="inputStap"
      :title="inputStapObject.data.type"
      size="lg"
      @ok="() => updateTaak(inputStapObject)"
      @hide="inputStap = false"
    >
      <component
        :is="inputStapObject.data.type"
        v-if="inputStapObject != null"
        :totaal="Number(opdracht.bedrag)"
        :activiteit="inputStapObject.data"
      />
      <template #modal-footer="{ ok }">
        <b-button
          class="mr-auto"
          variant="danger"
          @click="deleteTaak(inputStapObject)"
        >
          Verwijderen
        </b-button>
        <b-button 
          variant="success" 
          @click="ok()"
        >
          Opslaan
        </b-button>
      </template>
    </b-modal>

    <template #modal-footer="{ ok, cancel }">
      <b-button
        :disabled="opdracht.events.length > 0"
        class="mr-auto"
        variant="danger"
        @click="deleteOpdracht()"
      >
        Verwijderen
      </b-button>

      <b-button 
        variant="secondary" 
        @click="cancel()"
      >
        Annuleren
      </b-button>

      <b-button 
        variant="success" 
        @click="ok()"
      >
        Opslaan
      </b-button>
    </template>
  </b-modal>
</template>
<script>
import { verkoopMixin } from "@/mixins/verkoopMixin";
import SecureUpload from "../documenten/SecureUpload.vue";
import FontAwesome from "../../icons/FontAwesome.vue";
import Facturatie from "../../../components/trajecten/FacturatieStap.vue";
import NulInspectie from "../../../components/trajecten/InspectieStap.vue";
import Uitvoering from "../../../components/trajecten/UitvoeringStap.vue";

import Standaard from "../../../components/trajecten/StandaardStap.vue";
import Algemeen from "../../../components/trajecten/StandaardStap.vue";
import Unit from "../../../components/trajecten/StandaardStap.vue";

import UitvoeringWizard from "../../../components/trajecten/UitvoeringWizard.vue";
import UnitSelect from '../../../components/UnitSelect.vue';
import TrajectMenu from '../../../components/trajecten/TrajectMenu.vue';
import InspecteurSelect from '@/components/InspecteurSelect.vue';

export default {
  name: "Opdracht",
  components: { SecureUpload, FontAwesome, Facturatie, NulInspectie, TrajectMenu, Uitvoering, Standaard, Algemeen, UitvoeringWizard, Unit, UnitSelect, InspecteurSelect },
  mixins: [verkoopMixin],
  props: {
    id: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      visible: true,
      inputStap: false,
      inputStapObject: null,
      selectedTab: null,
      selectedUnit: null,
      opdracht: {
        nummer: null,
        relatie: null,
        toegewezen_aan: null,
        naam: null,
        adres: null,
        activiteiten: [],
        opdrachtnummer: null,
        opdrachtbestand: null,
        datum: null,
        bedrag: null,
        omschrijving: null,
        bestand: null,
        opdrachtbevestiging: null,
        status: "OPDRACHT",
        events: [],
      },
    };
  },
  computed: {
    opdrachtPath: function () {
      if (this.id != null) return `/verkoop/traject/${this.id}`;
    },
    authDocPath: function () {
      return `/documenten/donkenliftadvies/${new Date().getUTCFullYear()}/authenticated`;
    },
    takenPath: function () {
      return this.opdrachtPath + "/activiteiten";
    },
  },
  watch: {
    id: {
      immediate: true,
      handler: function (newval) {
        console.log("trigger id", newval);
        if (newval !== null) this.getOpdracht(newval);
      },
    },
  },
  methods: {
    deleteTaak(taak) {
      var vm = this;
      console.log(taak);
      this.deleteTrajectTaak(this.takenPath, { payload: taak }).then(() => {
        vm.opdracht.events.splice(vm.opdracht.events.indexOf(taak), 1);
        vm.inputStap = false;
        vm.inputStapObject = null;
      });
    },
    deleteOpdracht() {
      console.log("verwijderen...");
      var vm = this;
      this.deleteTraject(this.opdrachtPath, { opdracht: this.opdracht })
        .then(function (response) {
          console.log(response.data);
          vm.emitDelete(vm.opdracht);
        }
      );
    },
    insertTaak(type) {
      var vm = this;
      this.insertTrajectTaak(this.takenPath, {
        payload: { data: { type: type.value } },
      }).then(function (response) {
        console.log("ingevoerd");
        console.log(response.data);
        var taak = response.data.taak;
        taak.data.omschrijving = type.label;
        vm.opdracht.events.push(taak);
        vm.showDialogEdit(taak);
      });
    },
    onUitvoeringCompleted(result) {
      console.log(result);
      this.opdracht.events.push(...result.planning)
      this.selectedTab = 0
    },
    openConceptUrl(newFactuurId) {
      const routeData = this.$router.resolve({
        name: 'Factuur', params: { 
          id: newFactuurId
        }
      });
      window.open(routeData.href, '_blank');
    },
    createInvoice(args) {
      
      console.log("createInvoice", args.amount);

      var factuur = {
        FactuurDatum: new Date(),
        Prijspeiljaar: new Date().getUTCFullYear(),
        Relatie: { Id: this.opdracht.relatie.id },
        Herhalen: false,
        AutomatischUitsturen: false, // <!-- standaard staat automatisch uisturen niet aan.
        TemplatePath: 'preset 1 (standaard)',
        Onderwerp: this.opdracht.naam,
        VerzendenNaar: '', // <-- dit moet ingevuld worden op de factuur
        Details: {
          adres: null,
          eigenschappen: {
            ExterneReferentie: this.opdracht.opdrachtnummer,
            FactuurStandaardBTW: 21,
            Betalingsvoorwaarden: "Betaling binnen 30 dagen.",
            Omschrijving: "Hierbij onze factuur:",
            OnderwerpBovenAdres: true, // <-- zo staat de opdrachtnaam altijd boven het adres.
            Bijlagen: [],
          },
        },
        Factuurregels: [
          {
            Aantal: 1,
            Omschrijving: this.opdracht.activiteiten.join(", "),
            ExtraOmschrijving:
              `Locatieadres: ${this.opdracht.adres}`,
            Bedrag: args.amount,
          },
        ],
      }
      
      var vm = this
      vm.busy = true
      this.post(
          "/facturen/" + factuur.Prijspeiljaar,{ factuur: factuur })
        .then(function (response) {
          if (response.data.resultaat.RemoteId) {
            vm.openConceptUrl(response.data.resultaat.RemoteId)
            // vm.$router.push(
            //   "/facturatie/factuur/" + response.data.resultaat.RemoteId
            // );
          } else {
            console.log(response.data)
          }
        })
        .catch(function (error) {
          console.log(error);
        })
        .then(function () {
          vm.busy = false;
        });
    },
    updateTaak(taak) {
      // var vm = this;
      this.updateTrajectTaak(this.takenPath, { payload: taak }).then(function (
        response
      ) {
        console.log("geupdate");
        taak = response.data.taak;
        console.log(response.data);
      });
    },
    showDialogEdit(taak) {
      this.inputStap = true;
      this.inputStapObject = taak;
    },
    onOk(modal) {
      modal.preventDefault();
      var vm = this;
      this.updateTraject(this.opdrachtPath, { opdracht: this.opdracht }).then(
        function (response) {
          vm.emitUpdate(response.data.opdracht);
        }
      );
    },
    
    onHide(modal) {
      console.log(modal.trigger);
      if (modal.trigger !== "ok") this.goBack();
    },
    getOpdracht(opdracht_id) {
      var vm = this;
      this.getTraject("/verkoop/traject/" + opdracht_id).then(function (
        response
      ) {
        vm.opdracht = response.data.opdracht;
      });
    },
  },
};
</script>